import { ApiService } from "./ApiService";
import { DisputeDto } from "@shared/types/DisputeDto";

interface SubmitDisputePayload {
  category: string;
  description: string;
  requestedOutcome: string;
  images?: ImageUploadPayload[];
}

interface ImageUploadPayload {
  fileName: string;
  data: string;
  contentType: string;
}

interface AddCommentPayload {
  disputeId: string;
  content: string;
  images?: ImageUploadPayload[];
}

class DisputeService {
  constructor(private readonly apiService: ApiService) {}

  async getDisputes(): Promise<DisputeDto[]> {
    const data = await this.apiService.request<DisputeDto[]>("/disputes/list", {
      method: "GET",
      requireAuth: true,
    });
    return data.map((d: any) => ({
      ...d,
      createdAt: new Date(d.createdAt),
      updatedAt: new Date(d.updatedAt),
      resolvedAt: d.resolvedAt ? new Date(d.resolvedAt) : undefined,
      attachments: (d.attachments || []).map((a: any) => ({
        ...a,
        uploadedAt: new Date(a.uploadedAt),
      })),
      history: (d.history || []).map((h: any) => ({
        ...h,
        timestamp: new Date(h.timestamp),
      })),
    }));
  }

  async submitDispute(
    payload: SubmitDisputePayload
  ): Promise<{ success: boolean; disputeId: string }> {
    const body: {
      category: string;
      description: string;
      requestedOutcome: string;
      images?: ImageUploadPayload[];
    } = {
      category: payload.category,
      description: payload.description,
      requestedOutcome: payload.requestedOutcome,
    };
    if (payload.images && payload.images.length > 0) {
      body.images = payload.images;
    }
    return this.apiService.request<{ success: boolean; disputeId: string }>(
      "/disputes/submit",
      {
        method: "POST",
        body: JSON.stringify(body),
        requireAuth: true,
      }
    );
  }

  async addComment(payload: AddCommentPayload): Promise<{ success: boolean }> {
    const body: { content: string; images?: ImageUploadPayload[] } = {
      content: payload.content,
    };
    if (payload.images && payload.images.length > 0) {
      body.images = payload.images;
    }
    return this.apiService.request<{ success: boolean }>(
      `/disputes/add-comment/${payload.disputeId}`,
      {
        method: "POST",
        body: JSON.stringify(body),
        requireAuth: true,
      }
    );
  }

  static async filesToBase64(files: File[]): Promise<ImageUploadPayload[]> {
    return Promise.all(
      files.map(
        (file) =>
          new Promise<ImageUploadPayload>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const base64 = (reader.result as string).split(",")[1];
              resolve({
                fileName: file.name,
                data: base64,
                contentType: file.type,
              });
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          })
      )
    );
  }

  async acceptResolution(disputeId: string): Promise<{ success: boolean }> {
    return this.apiService.request<{ success: boolean }>(
      `/disputes/accept-resolution/${disputeId}`,
      {
        method: "POST",
        requireAuth: true,
      }
    );
  }

  async escalateToMediation(disputeId: string): Promise<{ success: boolean }> {
    return this.apiService.request<{ success: boolean }>(
      `/disputes/escalate/${disputeId}`,
      {
        method: "POST",
        requireAuth: true,
      }
    );
  }
}

export default DisputeService;
